import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const PLUS_PLANS = new Set(["intermediario", "pro", "enterprise"]);

export function isPlusPlanAllowed(plan?: string | null): boolean {
  return PLUS_PLANS.has((plan || "inicial").toLowerCase());
}

/** @deprecated use isPlusPlanAllowed */
export function isProductImagesPlanAllowed(plan?: string | null): boolean {
  return isPlusPlanAllowed(plan);
}

/** Workspaces granted product images despite their plan (commercial exception). */
export const PRODUCT_IMAGES_EXCEPTION_WORKSPACES = new Set([
  "9ff8841e-9733-40be-8578-47c7f477d599", // viniferreiramendes@gmail.com
]);

export function isProductImagesAllowedForWorkspace(
  workspaceId: string | null | undefined,
  plan?: string | null,
): boolean {
  if (workspaceId && PRODUCT_IMAGES_EXCEPTION_WORKSPACES.has(workspaceId)) return true;
  return isPlusPlanAllowed(plan);
}

const cache = new Map<string, string | null>();

export function useWorkspacePlan(workspaceId: string | null | undefined) {
  const [plan, setPlan] = useState<string | null>(() =>
    workspaceId ? (cache.get(workspaceId) ?? null) : null,
  );
  const [loading, setLoading] = useState<boolean>(!workspaceId ? false : !cache.has(workspaceId));

  useEffect(() => {
    let alive = true;
    if (!workspaceId) {
      setPlan(null);
      setLoading(false);
      return;
    }
    if (cache.has(workspaceId)) {
      setPlan(cache.get(workspaceId) ?? null);
      setLoading(false);
      return;
    }
    setLoading(true);
    supabase
      .from("workspaces")
      .select("plan")
      .eq("id", workspaceId)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        const p = (data?.plan as string | undefined) ?? null;
        cache.set(workspaceId, p);
        setPlan(p);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [workspaceId]);

  return {
    plan,
    loading,
    productImagesAllowed: isProductImagesAllowedForWorkspace(workspaceId, plan),
    plusPlanAllowed: isPlusPlanAllowed(plan),
  };
}
